import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { selectUser } from '../Commonlibs/userSlice';
import axios from 'axios';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  FormControl,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import propertyImage from '../assets/transacprop.png'; 
import Loader from '../Loader/Loader';

// Styled Card
const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
  borderRadius: '12px',
  padding: '16px',
  border: '2px solid #E5E7EB',
  minWidth: '280px',
  width: '100%',
}));

// Styled Tab
const StyledTab = styled(Box)(({ active }) => ({
  backgroundColor: active ? '#1976d2' : '#f5f5f7',
  color: active ? 'white' : '#4B5563',
  padding: '8px 24px',
  borderRadius: '8px',
  cursor: 'pointer',
  fontFamily: 'Outfit, sans-serif',
  fontSize: '14px',
  fontWeight: 500,
  textAlign: 'center',
  minWidth: '80px',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: active ? '#1565c0' : '#e0e0e0',
  },
}));

// Status Chip
const StatusChip = ({ status }) => {
  const getTextStyle = () => {
    switch (status) {
      case 'active':
      case 'Active':
        return { color: '#2e7d2e' };
      case 'completed':
      case 'Completed':
        return { color: '#1976d2' };
      case 'pending':
      case 'Pending':
      case 'created':
        return { color: '#f57c00' };
      case 'cancelled':
      case 'Cancelled':
        return { color: '#d32f2f' };
      default:
        return { color: '#616161' };
    }
  };

  // Convert status for display
  const displayStatus = () => {
    switch (status) {
      case 'created':
        return 'Pending';
      case 'active':
        return 'Active';
      case 'completed':
        return 'Completed';
      case 'cancelled':
        return 'Cancelled';
      default:
        return status.charAt(0).toUpperCase() + status.slice(1);
    }
  };

  return (
    <Typography
      sx={{
        ...getTextStyle(),
        fontWeight: 500,
        fontSize: '13px',
        fontFamily: 'Outfit, sans-serif',
      }}
    >
      {displayStatus()}
    </Typography>
  );
};

// Custom Pagination
const CustomPagination = ({ currentPage, totalPages, onPageChange }) => {
  const handlePrevious = () => {
    if (currentPage > 1) {
      onPageChange(currentPage - 1);
    }
  };
  const handleNext = () => {
    if (currentPage < totalPages) {
      onPageChange(currentPage + 1);
    }
  };
  const handlePageClick = (page) => {
    onPageChange(page);
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Box
        onClick={handlePrevious}
        sx={{
          padding: '8px 12px',
          cursor: currentPage > 1 ? 'pointer' : 'not-allowed',
          color: currentPage > 1 ? '#1976d2' : '#4B5563',
          fontFamily: 'Outfit, sans-serif',
          fontSize: '14px',
          fontWeight: 500,
          '&:hover': {
            backgroundColor: currentPage > 1 ? '#f5f5f5' : 'transparent',
          },
          borderRadius: '8px',
          border: '2px solid #E5E7EB',
        }}
      >
        Previous
      </Box>
      {[...Array(totalPages)].map((_, index) => {
        const page = index + 1;
        return (
          <Box
            key={page}
            onClick={() => handlePageClick(page)}
            sx={{
              width: '32px',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              backgroundColor: currentPage === page ? '#1976d2' : 'transparent',
              color: currentPage === page ? 'white' : '#666',
              borderRadius: '4px',
              fontFamily: 'Outfit, sans-serif',
              fontSize: '14px',
              fontWeight: 500,
              border: '1px solid #e0e0e0',
              '&:hover': {
                backgroundColor: currentPage === page ? '#1565c0' : '#f5f5f5',
              },
            }}
          >
            {page}
          </Box>
        );
      })}
      <Box
        onClick={handleNext}
        sx={{
          padding: '8px 12px',
          cursor: currentPage < totalPages ? 'pointer' : 'not-allowed',
          color: currentPage < totalPages ? '#4B5563' : '#4B5563',
          fontFamily: 'Outfit, sans-serif',
          fontSize: '14px',
          fontWeight: 500,
          '&:hover': {
            backgroundColor: currentPage < totalPages ? '#f5f5f5' : 'transparent',
          },
          borderRadius: '8px',
          border: '2px solid #E5E7EB',
        }}
      >
        Next
      </Box>
    </Box>
  );
};

// Helper function to calculate duration
const calculateDuration = (startDate, endDate) => {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const today = new Date();
  
  if (today > end) {
    return 'Ended';
  }
  
  const daysLeft = Math.ceil((end - today) / (1000 * 60 * 60 * 24));
  return `${daysLeft} Days left`;
};

// Main Component
const Transactions = () => {
  const [selectedTab, setSelectedTab] = useState('All');
  const [dateRange, setDateRange] = useState('Last 30 days');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [transactionData, setTransactionData] = useState([]);
  const [statsData, setStatsData] = useState([
    { title: 'Total Spent', value: '₹0' },
    { title: 'Currently Active', value: '0' },
    { title: 'Total Promotions', value: '0' },
  ]);
  
  // Get the user from Redux store
  const user = useSelector(selectUser);
  const seller_id = user?.id || 1; // Default to 1 if not available
  
  const handleTabChange = (tabName) => {
    setSelectedTab(tabName);
  };
  const handleDateChange = (event) => {
    setDateRange(event.target.value);
  };
  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchTransactions(page);
  };
  const tabLabels = ['All', 'Active', 'Completed', 'Pending', 'Cancelled'];
  
  // Mock transaction data for UI display when API returns empty
  const mockTransactionData = [
    {
      id: 1,
      property: 'Modern Apartment',
      transactionId: 'Txn:PTP700Q90Q653Q01',
      date: '30.06.2025 10:25PM',
      plan: 'Basic Plan',
      amount: '₹139/month',
      status: 'Active',
      duration: '15 Days left',
      image: propertyImage,
    },
    {
      id: 2,
      property: 'Modern Apartment',
      transactionId: 'Txn:PTP700Q90Q653Q01',
      date: '30.06.2025 10:25PM',
      plan: 'Professional Plan',
      amount: '₹499/month',
      status: 'Completed',
      duration: 'Ended 2 days ago',
      image: propertyImage,
    },
    {
      id: 3,
      property: 'Modern Apartment',
      transactionId: 'Txn:PTP700Q90Q653Q01',
      date: '30.06.2025 10:25PM',
      plan: 'Enterprise Plan',
      amount: '₹999/month',
      status: 'Pending',
      duration: 'Start in 3 days',
      image: propertyImage,
    },
    {
      id: 4,
      property: 'Modern Apartment',
      transactionId: 'Txn:PTP700Q90Q653Q01',
      date: '30.06.2025 10:25PM',
      plan: 'Enterprise Plan',
      amount: '₹999/month',
      status: 'Cancelled',
      duration: '---',
      image: propertyImage,
    },
  ];

  const fetchTransactions = async (page = 1) => {
    setLoading(true);
    try {
      const response = await axios.get(
        `https://propertydealstalk.com/api/seller/getSubscriptionTransactionHistory?seller_id=${seller_id}&page=${page}`
      );
      
      if (response.data.status === "SUCCESS") {
        const transactions = response.data.result.transactions;
        
        if (transactions && transactions.length > 0) {
          // Calculate total amount spent
          const totalSpent = transactions.reduce((sum, transaction) => {
            return sum + (transaction.plan ? transaction.plan.price_per_month : 0);
          }, 0);
          
          // Count active subscriptions
          const activeCount = transactions.filter(transaction => 
            transaction.subscription && transaction.subscription.active
          ).length;
          
          // Map API response to component's expected format
          const formattedTransactions = transactions.map(transaction => {
            // Format the transaction date
            const txDate = new Date(transaction.transaction_date);
            const formattedDate = `${txDate.toLocaleDateString()} ${txDate.toLocaleTimeString()}`;
            
            // Calculate duration if subscription exists
            let durationText = '---';
            if (transaction.subscription) {
              durationText = calculateDuration(
                transaction.subscription.start_date,
                transaction.subscription.end_date
              );
            }
            
            return {
              id: transaction.id,
              property: transaction.property ? transaction.property.name : 'Property',
              transactionId: `Order:${transaction.razorpay_order_id}`,
              date: formattedDate,
              plan: transaction.plan ? transaction.plan.name : 'Basic Plan',
              amount: `₹${transaction.plan ? transaction.plan.price_per_month : 0}/month`,
              status: transaction.status,
              duration: durationText,
              image: transaction.property ? transaction.property.image : propertyImage,
            };
          });
          
          setTransactionData(formattedTransactions);
          
          // Set pagination data
          setTotalPages(response.data.result.total_pages || 1);
          
          // Update stats
          setStatsData([
            { title: 'Total Spent', value: `₹${totalSpent}` },
            { title: 'Currently Active', value: `${activeCount}` },
            { title: 'Total Promotions', value: `${response.data.result.total || 0}` },
          ]);
        } else {
          // When no transactions found
          setTransactionData([]);
          setTotalPages(1);
          setStatsData([
            { title: 'Total Spent', value: '₹0' },
            { title: 'Currently Active', value: '0' },
            { title: 'Total Promotions', value: '0' },
          ]);
        }
      } else {
        // When API returns error
        setTransactionData([]);
        setTotalPages(1);
        setStatsData([
            { title: 'Total Spent', value: '₹0' },
            { title: 'Currently Active', value: '0' },
            { title: 'Total Promotions', value: '0' },
          ]);
      }
    } catch (error) {
      console.error("Error fetching transactions:", error);
      // On error
      setTransactionData([]);
      setTotalPages(1);
      setStatsData([
        { title: 'Total Spent', value: '₹0' },
        { title: 'Currently Active', value: '0' },
        { title: 'Total Promotions', value: '0' },
      ]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTransactions(currentPage);
  }, [seller_id]); // Fetch when seller_id changes

  // Filter transactions based on selected tab
  const filteredTransactions = transactionData.filter(transaction => {
    if (selectedTab === 'All') return true;
    
    // Map API status to UI status
    const statusMap = {
      'created': 'Pending',
      'active': 'Active',
      'completed': 'Completed',
      'cancelled': 'Cancelled'
    };
    
    const mappedStatus = statusMap[transaction.status] || transaction.status;
    return mappedStatus === selectedTab;
  });

  return (
    <Box
      sx={{
        backgroundColor: '#f8f9fa',
        minHeight: '100vh',
        fontFamily: 'Outfit, sans-serif',
        width: '100%',
        ml: { xs: '20px', md: '20px' },
        mr: { xs: '20px', md: '80px' },
      }}
    >
      {loading && <Loader />}
      
      <Box
        sx={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '24px',
          mt: '25px',
          mb: '20px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
        }}
      >
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {statsData.map((stat, index) => (
            <Grid
              item
              xs={12}
              md={4}
              key={index}
              sx={{
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <StyledCard>
                <CardContent sx={{ p: '16px' }}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      mb: 1,
                      fontFamily: 'Outfit, sans-serif',
                      fontSize: '14px',
                      fontWeight: 400,
                    }}
                  >
                    {stat.title}
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{
                      fontFamily: 'Outfit, sans-serif',
                      fontWeight: 600,
                      fontSize: '24px',
                      color: '#000',
                    }}
                  >
                    {stat.value}
                  </Typography>
                </CardContent>
              </StyledCard>
            </Grid>
          ))}
        </Grid>
        {/* Filters */}
        <Box sx={{ mb: 3 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 2,
            }}
          >
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <Select
                value={dateRange}
                onChange={handleDateChange}
                sx={{
                  backgroundColor: 'white',
                  '& .MuiOutlinedInput-notchedOutline': {
                    border: '1px solid #e0e0e0',
                  },
                  fontFamily: 'Outfit, sans-serif',
                  fontSize: '14px',
                  borderRadius: '8px',
                  height: '40px',
                }}
              >
                <MenuItem value="Last 30 days" sx={{ fontFamily: 'Outfit, sans-serif' }}>
                  Last 30 days
                </MenuItem>
                <MenuItem value="Last 7 days" sx={{ fontFamily: 'Outfit, sans-serif' }}>
                  Last 7 days
                </MenuItem>
                <MenuItem value="Last 90 days" sx={{ fontFamily: 'Outfit, sans-serif' }}>
                  Last 90 days
                </MenuItem>
              </Select>
            </FormControl>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              {tabLabels.map((label) => (
                <StyledTab
                  key={label}
                  active={selectedTab === label}
                  onClick={() => handleTabChange(label)}
                >
                  {label}
                </StyledTab>
              ))}
            </Box>
          </Box>
        </Box>
        {/* Table */}
        <Box sx={{ position: 'relative' }}>
          <TableContainer
            component={Paper}
            sx={{
              borderRadius: '12px',
              boxShadow: 'none',
              border: '2px solid #e0e0e0',
              overflowX: 'auto',
            }}
          >
            <Table
              sx={{
                borderCollapse: 'separate',
                borderSpacing: 0,
                '& td, & th': {
                  borderBottom: 'none',
                },
              }}
            >
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f7' }}>
                  {['Property', 'Promotion Plan', 'Amount', 'Status', 'Duration'].map((head) => (
                    <TableCell
                      key={head}
                      sx={{
                        fontFamily: 'Outfit, sans-serif',
                        color: '#666',
                        fontWeight: 500,
                        fontSize: '14px',
                        padding: '16px',
                      }}
                    >
                      {head}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredTransactions.length > 0 ? (
                  filteredTransactions.map((row) => (
                    <TableRow key={row.id} hover sx={{ '&:hover': { backgroundColor: '#f9f9f9' } }}>
                      <TableCell sx={{ padding: '16px' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar
                            src={row.image}
                            alt={row.property}
                            sx={{ width: 58, height: 58, borderRadius: '8px' }}
                            variant="rounded"
                          />
                          <Box>
                            <Typography
                              variant="subtitle2"
                              fontWeight="600"
                              sx={{
                                fontFamily: 'Outfit, sans-serif',
                                fontSize: '14px',
                                color: '#4B5563',
                                mb: 0.5,
                              }}
                            >
                              {row.property}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{
                                fontFamily: 'Outfit, sans-serif',
                                display: 'block',
                                fontSize: '12px',
                                mb: 0.25,
                              }}
                            >
                              {row.transactionId}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{
                                fontFamily: 'Outfit, sans-serif',
                                fontSize: '12px',
                              }}
                            >
                              {row.date}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ padding: '16px' }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: 'Outfit, sans-serif',
                            fontSize: '14px',
                            color: '#4B5563',
                          }}
                        >
                          {row.plan}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ padding: '16px' }}>
                        <Typography
                          variant="body2"
                          fontWeight="600"
                          sx={{
                            fontFamily: 'Outfit, sans-serif',
                            fontSize: '14px',
                            color: '#000',
                          }}
                        >
                          {row.amount.split('/')}
                          <Typography
                            component="span"
                            sx={{
                              fontFamily: 'Outfit, sans-serif',
                              fontSize: '13px',
                              fontWeight: 400,
                              color: '#4B5563',
                              ml: 0.5,
                            }}
                          >
                            /month
                          </Typography>
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ padding: '16px' }}>
                        <StatusChip status={row.status} />
                      </TableCell>
                      <TableCell sx={{ padding: '16px' }}>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            fontFamily: 'Outfit, sans-serif',
                            fontSize: '14px',
                          }}
                        >
                          {row.duration}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ padding: '32px 16px' }}>
                      <Typography
                        variant="body1"
                        sx={{
                          fontFamily: 'Outfit, sans-serif',
                          fontSize: '16px',
                          color: '#4B5563',
                        }}
                      >
                        No transactions found
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {/* Pagination */}
          {totalPages > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
              <CustomPagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
              />
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default Transactions;